/**
 * volumePick.js — какой том считать клиническим расчётам. [12.0.52]
 *
 * Чистая логика: кэш и окна передаются параметрами (в программе — кэш
 * Cornerstone и окна движка), поэтому проверяется на подменах
 * (tests/clinical_test.mjs).
 *
 * ПОВОД. Прежний getVolumeData (ui/clinicalTools.js) брал данные у ПЕРВОГО
 * актёра первого окна, у которого есть скаляры. У объёмных окон Cornerstone
 * 5.6 скаляров на актёре нет (ImageVolume: hasScalarVolume:false), а у
 * СТЕКОВОГО окна есть — это ОДИН текущий снимок (planarImageRendering:
 * размеры [ширина, высота, 1]). В раскладке «2D Стопка» Агатстон, эмфизема и
 * КТИ считались по одному срезу и выдавались за объём. Последний запасной
 * путь брал «первый том из кэша» — им могла оказаться другая серия или
 * маска сегментации.
 *
 * ТЕПЕРЬ: том — поимённо (volumeId исследования), иначе — том активного
 * окна MPR, иначе — любого объёмного окна. Маски сегментации пропускаются.
 * Стековых снимков и «чего-нибудь из кэша» нет: не нашли — отказ словами.
 *
 * EXPORTS: найтиТом, этоМаска, данныеТома, требуетсяКТ, индексВМир, мирВИндекс, ПРЕФИКС
 */

export const ПРЕФИКС = 'cornerstoneStreamingImageVolume:';

/** Том — маска сегментации, а не снимок? */
export function этоМаска(id, том) {
  if (/labelmap|mikhmel-seg|segmentation/i.test(String(id || том?.volumeId || ''))) return true;
  return !!(том?.isLabelmap || том?.referencedVolumeId && /seg/i.test(String(том.volumeId || '')));
}

function изКэша(кэш, id) {
  if (!id || !кэш?.getVolume) return null;
  const s = String(id);
  const варианты = s.startsWith(ПРЕФИКС) ? [s, s.slice(ПРЕФИКС.length)] : [s, ПРЕФИКС + s];
  for (const в of варианты) {
    const т = кэш.getVolume(в);
    if (т && !этоМаска(в, т)) return { том: т, id: в };
  }
  return null;
}

/**
 * Найти том.
 * @param {{getVolume:Function}} кэш
 * @param {string|null} volumeId — том исследования (может быть пустым)
 * @param {Array<{id:string, type:string, getActors?:Function}>} окна — окна, активное первым
 * @returns {{ok:true, том:object, id:string, откуда:string}|{ok:false, причина:string}}
 */
export function найтиТом(кэш, volumeId, окна = []) {
  const поИмени = изКэша(кэш, volumeId);
  if (поИмени) return { ok: true, ...поИмени, откуда: 'том исследования' };
  let былоСтековое = false;
  for (const окно of окна) {
    if (!окно) continue;
    const тип = String(окно.type || '').toLowerCase();
    if (тип === 'stack') { былоСтековое = true; continue; }
    if (тип !== 'orthographic' && тип !== 'volume3d') continue;
    let актёры = [];
    try { актёры = окно.getActors?.() || []; } catch (e) { актёры = []; }
    for (const а of актёры) {
      const id = а?.referencedId || а?.uid || а?.referenceId || а?.volumeId;
      const н = изКэша(кэш, id);
      if (н) return { ok: true, ...н, откуда: `окно «${окно.id}»` };
    }
  }
  return {
    ok: false,
    причина: былоСтековое
      ? 'открыта раскладка со стопкой снимков, а расчёту нужен объём — включите MPR (кнопка раскладки) и повторите'
      : 'объём не загружен — откройте серию в раскладке MPR',
  };
}

/** Данные тома: массив значений, размеры, шаг, начало, направление. */
export function данныеТома(том) {
  let d = null;
  try { d = том?.voxelManager?.getCompleteScalarDataArray?.() || null; } catch (e) { d = null; }
  if (!d) { try { d = том?.imageData?.getPointData?.()?.getScalars?.()?.getData?.() || null; } catch (e) { d = null; } }
  if (!d) { try { d = том?.getScalarData?.() || null; } catch (e) { d = null; } }
  const dims = том?.dimensions || том?.imageData?.getDimensions?.();
  if (!d || !dims || d.length < dims[0] * dims[1] * dims[2]) return null;
  const dir = том.direction || том.imageData?.getDirection?.();
  return {
    scalarData: d,
    dims: Array.from(dims),
    spacing: Array.from(том.spacing || том.imageData?.getSpacing?.() || [1, 1, 1]),
    origin: Array.from(том.origin || том.imageData?.getOrigin?.() || [0, 0, 0]),
    direction: dir && dir.length === 9 ? Array.from(dir) : [1, 0, 0, 0, 1, 0, 0, 0, 1],
    imageData: том.imageData || null,
    modality: том.metadata?.Modality || null,
  };
}

/**
 * Расчёт в единицах HU годится только для КТ. Модальность неизвестна — не
 * запрещаем (у части загрузок её нет), но и не молчим: решает вызывающий.
 * @returns {string|null} причина отказа или null
 */
export function требуетсяКТ(данные, что = 'Расчёт') {
  const м = String(данные?.modality || '').toUpperCase();
  if (м && м !== 'CT') return `${что} считается по плотности в HU — это есть только у КТ, а открыта серия ${м}`;
  return null;
}

/**
 * Индекс вокселя → мир (мм) с учётом направления осей тома.
 * Cornerstone хранит direction как три оси подряд: ось i, ось j, ось k.
 */
export function индексВМир(данные, ijk) {
  const o = данные.origin || [0, 0, 0], s = данные.spacing || [1, 1, 1];
  const D = данные.direction || [1, 0, 0, 0, 1, 0, 0, 0, 1];
  const a = ijk[0] * s[0], b = ijk[1] * s[1], c = ijk[2] * s[2];
  return [0, 1, 2].map((r) => o[r] + D[r] * a + D[3 + r] * b + D[6 + r] * c);
}

/** Мир (мм) → индекс вокселя (дробный). Оси ортонормированы — обратная = транспонированная. */
export function мирВИндекс(данные, мир) {
  const o = данные.origin || [0, 0, 0], s = данные.spacing || [1, 1, 1];
  const D = данные.direction || [1, 0, 0, 0, 1, 0, 0, 0, 1];
  const d = [мир[0] - o[0], мир[1] - o[1], мир[2] - o[2]];
  return [0, 1, 2].map((ось) => (D[3 * ось] * d[0] + D[3 * ось + 1] * d[1] + D[3 * ось + 2] * d[2]) / (s[ось] || 1));
}

export default { найтиТом, этоМаска, данныеТома, требуетсяКТ, индексВМир, мирВИндекс, ПРЕФИКС };
